import { MEMBER_STATUS, PENDING_MEMBER_STATUSES, USER_TYPE } from '../constants/copy.js';
import {
  MEMBER_ACTION,
  MEMBER_ACTION_PERMISSION,
  MEMBER_ACTIONS_BY_STATUS,
  MEMBER_BULK_ACTION,
  MEMBER_BULK_ACTION_PERMISSION,
} from '../constants/memberActions.js';
import { UNAUTHORIZED_MODES } from '../constants/permissionMode.js';
import { PERMISSIONS } from '../constants/permissions.js';
import { hasPermission } from './hasPermission.js';

/**
 * The ONE place that decides what can be done to a member or a selection. No component branches
 * on status or user type. Every entry is `{ id, labelKey, enabled, disabledReasonKey?, variant? }`
 * (`labelKey` / `disabledReasonKey` are i18n keys of the `people-access` namespace).
 *
 * Permission rule (CT-5569): an action the actor may not run is omitted (mode `hide`, also when no
 * `mode` is given) or returned disabled with the "no access" explanation (mode `lock`). Callers pass
 * the mode they got from `usePermissions`, never `DEFAULT_UNAUTHORIZED_MODE` itself.
 *
 * @typedef {{ user_type: string, permissions: string[] }} Actor
 */

const REASON = Object.freeze({
  locked: 'guard.locked',
  ownerNotSuspendable: 'members.reasons.ownerNotSuspendable',
  ownerNotRemovable: 'members.reasons.ownerNotRemovable',
  removeAdminForbidden: 'members.reasons.removeAdminForbidden',
  alreadyRemoved: 'members.reasons.alreadyRemoved',
  sendInvitationsActive: 'members.reasons.sendInvitationsActive',
  suspendActiveOnly: 'members.reasons.suspendActiveOnly',
});

const isOwner = (member) => member.userType === USER_TYPE.OWNER;
const isAdmin = (member) => member.userType === USER_TYPE.ADMIN;

/**
 * Removing an Admin needs the dedicated permission, held explicitly (the Admin bypass of
 * `hasPermission` does not apply) or by the Owner. It never unlocks removing the Owner.
 */
const canRemoveAdmin = (actor) =>
  actor?.user_type === USER_TYPE.OWNER ||
  (actor?.permissions ?? []).includes(PERMISSIONS.MEMBERS_REMOVE_ADMIN);

/** Entry for an action; `null` when the actor lacks the permission and the mode is hide. */
const gate = (entry, permission, actor, mode) => {
  if (hasPermission(actor, permission)) return entry;
  if (mode === UNAUTHORIZED_MODES.LOCK) {
    return { ...entry, enabled: false, disabledReasonKey: REASON.locked };
  }
  return null;
};

const withReason = (entry, reasonKey) =>
  reasonKey ? { ...entry, enabled: false, disabledReasonKey: reasonKey } : { ...entry, enabled: true };

/** Reason a single row action is blocked for this member, or undefined. */
const rowActionBlock = (actionId, member) =>
  actionId === MEMBER_ACTION.SUSPEND && isOwner(member) ? REASON.ownerNotSuspendable : undefined;

/**
 * Kebab menu of one row.
 *
 * @param {{ status: string, userType: string }} member UI member.
 * @param {Actor} actor
 * @param {{ mode?: 'hide'|'lock' }} [options]
 */
export const getMemberRowActions = (member, actor, { mode } = {}) =>
  (MEMBER_ACTIONS_BY_STATUS[member.status] ?? [])
    .map((id) =>
      gate(
        withReason({ id, labelKey: `members.rowActions.${id}` }, rowActionBlock(id, member)),
        MEMBER_ACTION_PERMISSION[id],
        actor,
        mode,
      ),
    )
    .filter(Boolean);

/** Reason a bulk action is blocked for this selection, or undefined. */
const bulkActionBlock = (actionId, members, actor) => {
  switch (actionId) {
    case MEMBER_BULK_ACTION.SEND_INVITATIONS:
      return members.every((m) => PENDING_MEMBER_STATUSES.includes(m.status))
        ? undefined
        : REASON.sendInvitationsActive;
    case MEMBER_BULK_ACTION.SUSPEND:
      if (members.some(isOwner)) return REASON.ownerNotSuspendable;
      return members.every((m) => m.status === MEMBER_STATUS.ACTIVE)
        ? undefined
        : REASON.suspendActiveOnly;
    case MEMBER_BULK_ACTION.REMOVE:
      if (members.some(isOwner)) return REASON.ownerNotRemovable;
      if (members.some((m) => m.status === MEMBER_STATUS.REMOVED)) return REASON.alreadyRemoved;
      return members.some(isAdmin) && !canRemoveAdmin(actor) ? REASON.removeAdminForbidden : undefined;
    default:
      return undefined;
  }
};

const BULK_ACTIONS = Object.freeze([
  { id: MEMBER_BULK_ACTION.SEND_INVITATIONS },
  { id: MEMBER_BULK_ACTION.SUSPEND },
  { id: MEMBER_BULK_ACTION.REMOVE, variant: 'danger' },
]);

/**
 * Bulk bar over the selected rows: Send invitations, Suspend and Remove (red). An action that
 * cannot apply to EVERY selected row is disabled with its reason (the Owner is never silently
 * skipped).
 *
 * @param {{ status: string, userType: string }[]} members Selected UI members.
 * @param {Actor} actor
 * @param {{ mode?: 'hide'|'lock' }} [options]
 */
export const getBulkActions = (members, actor, { mode } = {}) => {
  if (!members.length) return [];

  return BULK_ACTIONS.map(({ id, variant }) =>
    gate(
      withReason(
        { id, labelKey: `members.bulkActions.${id}`, ...(variant ? { variant } : {}) },
        bulkActionBlock(id, members, actor),
      ),
      MEMBER_BULK_ACTION_PERMISSION[id],
      actor,
      mode,
    ),
  ).filter(Boolean);
};

/**
 * Whether the actor can do anything to a selection, so the table offers row checkboxes at all. In
 * `lock` mode the actions show disabled with the "no access" reason, so selecting stays useful.
 *
 * @param {Actor} actor
 * @param {{ mode?: 'hide'|'lock' }} [options]
 */
export const canSelectMembers = (actor, { mode } = {}) =>
  mode === UNAUTHORIZED_MODES.LOCK ||
  Object.values(MEMBER_BULK_ACTION_PERMISSION).some((permission) => hasPermission(actor, permission));
